import { Theme } from '@styled-system/css';
import { createContext, ReactNode, useContext } from 'react';

const C = {
  ElectricViolet: '#6E53F6',
  RoyalBlue: '#5D40F0',
  MountainMeadow: '#21B75D',
  Flamingo: '#EE5938',
  BrickRed: '#D52E4C',
  Selago: '#F0EEFE',
  Concrete: '#F2F2F2',
  Mercury: '#E5E5E5',
  Silver: '#CCCCCC',
  GreyAAA: '#AAAAAA',
  Boulder: '#757575',
  TulipTree: '#EEBB38',
};

const makeTheme = <T extends Theme>(t: T) => t;

const theme = makeTheme({
  space: [
    0, 4, 8, 12, 16, 20, 24, 28, 32, 36, 40, 44, 48, 52, 56, 60, 64,
  ] as const,
  fontSizes: [12, 14, 16, 20, 24, 40, 64] as const,
  fonts: {
    primary: '"Aeonik", sans-serif',
    decorative: '"EB Garamond", serif',
    monospace: `ui-monospace,
    Menlo, Monaco,
    "Cascadia Mono", "Segoe UI Mono",
    "Roboto Mono",
    "Oxygen Mono",
    "Ubuntu Monospace",
    "Source Code Pro",
    "Fira Mono",
    "Droid Sans Mono",
    "Courier New", monospace;`,
  },
  fontWeights: {
    normal: '400',
    medium: '500',
    bold: '700',
  },
  lineHeights: {
    body: 1.5,
    title: 1.2,
    heading: 1.33,
  },
  colors: {
    text: '#383838', // Body foreground color
    muted: C.Boulder, // Body FG with lower contrast to BG
    background: '#fff', // Body background color
    backgroundAlt: '#F8F8F8',
    primary: C.ElectricViolet, // Primary brand color for links, buttons, etc.
    primary100: C.ElectricViolet,
    primary10: C.Selago,

    secondary: C.RoyalBlue, // A secondary brand color for alternative styling
    highlight: C.Selago, //  background color for highlighting text
    grey10: C.Concrete,
    grey20: C.Mercury,
    grey30: C.Silver,
    grey40: C.GreyAAA,
    grey50: C.Boulder,
    transparent: 'transparent',

    success100: C.MountainMeadow,
    success10: '#E9F8EF',

    warning100: C.TulipTree,
    warning10: '#fdf4dd',

    error100: C.BrickRed,

    accent: C.Flamingo, // A contrast color for emphasizing UI
    accent100: C.Flamingo,
    accent10: '#FDEEEB',
    accent5: '#FEF7F5',

    black: 'black',
    white: 'white',
    white70: 'rgba(255,255,255,0.7)',
    white20: 'rgba(255,255,255,0.2)',
  },
  breakpoints: [
    // These are just copy-pasted from MUI's breakpoints for now
    '600px',
    '900px',
    '1200px',
    '1536xpx',
  ],
  zIndicies: [0, 10, 20, 40, 50],
});

export type DSTheme = typeof theme;
// HACK: Hand-coded becuase getting indicies from TS is mad
export type SpaceChoices = [
  0,
  1,
  2,
  3,
  4,
  5,
  6,
  7,
  8,
  9,
  10,
  11,
  12,
  13,
  14,
  15,
  16,
  -1,
  -2,
  -3,
  -4,
  -5,
  -6,
  -7,
  -8,
  -9,
  -10,
  -11,
  -12,
  -13,
  -14,
  -15,
  -16,
][number];
export type FontSizeChoices = [0, 1, 2, 3, 4, 5, 6][number];

// NOTE: Never statically import the theme. Use `useDSTheme` or DS components
export const __forStorybook = { theme };

const DSContext = createContext({
  theme,
});

export const SemperDSProvider = (props: { children: ReactNode }) => (
  <DSContext.Provider value={{ theme }} {...props} />
);
export const useDSTheme = () => useContext(DSContext);
